import { renderRichText } from "@storyblok/react";
import type {
  LocationPage,
  FaqSection,
  HeroWide,
  Title,
  SimpleRichText,
  LinedListSection,
  Accordion,
  AccordionItem,
  LinedListItemDetailed,
  Carousel,
  CardLocations,
  CardParking,
  LocationCard,
  LinedListItem,
} from "@/generated/sb";
import { parseLocationUrl, slugToName, parseAddress } from "@/lib/location";
import {
  generateCityPageSchema,
  generateNeighborhoodPageSchema,
  generateDestinationPageSchema,
  generateParkingLotPageSchema,
  generateFAQPageSchema,
  generateParkingItemListSchema,
  generateDestinationItemListSchema,
  getPOITypeFromName,
} from "./generators";
import { JsonLdScript } from "./JsonLdScript";
import { SchemaWebPage, SchemaFAQPage, SchemaItemList } from "./types";

/** Unwrap a link field that may be either a native MultilinkStoryblok or a LinkStoryblok[] */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapLink(value: any): any {
  return Array.isArray(value) ? value?.[0]?.link : value;
}

function stripHtmlTags(html: string): string {
  let stripped = String(html);
  let prev: string;
  do {
    prev = stripped;
    stripped = stripped.replace(/<[^>]*>/g, "");
  } while (stripped !== prev);
  return stripped;
}

export interface LocationPageSchemaProps {
  blok: LocationPage;
  pathname: string;
}

function extractNameFromHeroWide(heroWide: HeroWide): string | null {
  const typography = heroWide.typography;
  if (!typography || typography.length === 0) return null;

  for (const sectionTitle of typography) {
    const innerTypography = sectionTitle.typography;
    if (!innerTypography) continue;

    for (const item of innerTypography) {
      if (item.component === "title") {
        const titleItem = item as Title;
        if (titleItem.content) {
          return titleItem.content;
        }
      }
    }
  }

  return null;
}

function extractDescriptionFromInfoBlock(
  content: LocationPage["content"],
): string | null {
  if (!content) return null;

  for (const section of content) {
    if (section.component === "infoBlock") {
      const infoBlock = section as { text?: SimpleRichText[] };
      if (infoBlock.text && infoBlock.text.length > 0) {
        const firstText = infoBlock.text[0];
        if (firstText?.content) {
          const html = renderRichText(firstText.content);
          if (html) {
            return stripHtmlTags(html).slice(0, 300);
          }
        }
      }
    }
  }

  return null;
}

function extractDescriptionFromLocationCard(
  locationCard: LocationCard,
): string | null {
  if (!locationCard.description || locationCard.description.length === 0)
    return null;

  const firstText = locationCard.description[0] as SimpleRichText;
  if (firstText?.content) {
    const html = renderRichText(firstText.content);
    if (html) {
      return stripHtmlTags(html).slice(0, 300);
    }
  }

  return null;
}

function extractFaqItemsFromFaqSection(
  faqSection: FaqSection,
): Array<{ question: string; answer: string }> {
  const items: Array<{ question: string; answer: string }> = [];

  if (!faqSection.questions) return items;

  for (const question of faqSection.questions) {
    const questionText = question.title;
    if (!questionText) continue;

    const answerText = extractAnswerFromAccordionItem(question);
    if (answerText) {
      items.push({ question: questionText, answer: answerText });
    }
  }

  return items;
}

function extractFaqItemsFromLinedListSection(
  section: LinedListSection,
): Array<{ question: string; answer: string }> {
  const items: Array<{ question: string; answer: string }> = [];

  if (!section.items) return items;

  for (const item of section.items) {
    if (item.component === "accordion") {
      const accordion = item as Accordion;
      if (!accordion.items) continue;

      for (const accordionItem of accordion.items) {
        const questionText = accordionItem.title;
        if (!questionText) continue;

        const answerText = extractAnswerFromAccordionItem(accordionItem);
        if (answerText) {
          items.push({ question: questionText, answer: answerText });
        }
      }
    }
  }

  return items;
}

function extractAnswerFromAccordionItem(item: AccordionItem): string {
  if (!item.text || item.text.length === 0) return "";

  const htmlParts: string[] = [];
  for (const textBlok of item.text as SimpleRichText[]) {
    if (textBlok.content) {
      const html = renderRichText(textBlok.content);
      if (html) {
        htmlParts.push(html);
      }
    }
  }

  return htmlParts.join("");
}

function extractParkingOptionsFromLinedListSection(
  section: LinedListSection,
): Array<{ name: string; url: string }> {
  const items: Array<{ name: string; url: string }> = [];

  if (!section.items) return items;

  for (const item of section.items) {
    if (item.component === "linedListItemDetailed") {
      const detailedItem = item as LinedListItemDetailed;
      const name = detailedItem.title;
      const button = detailedItem.button?.[0];
      const buttonLink = unwrapLink(button?.link);
      // Prefer the live-resolved story URL so a renamed target's JSON-LD self-heals;
      // fall back to the frozen url/cached_url when no story is resolved.
      const url =
        buttonLink?.story?.url || buttonLink?.url || buttonLink?.cached_url;

      if (name && url) {
        items.push({ name, url });
      }
    }
  }

  return items;
}

function extractParkingOptionsFromCarousel(
  carousel: Carousel,
): Array<{ name: string; url: string }> {
  const items: Array<{ name: string; url: string }> = [];

  if (!carousel.cards) return items;

  for (const card of carousel.cards) {
    if (card.component === "cardParking") {
      const parkingCard = card as CardParking;

      const name = parkingCard.title;
      const button = parkingCard.button?.[0];
      const buttonLink = unwrapLink(button?.link);
      // Prefer the live-resolved story URL so a renamed target's JSON-LD self-heals;
      // fall back to the frozen url/cached_url when no story is resolved.
      const url =
        buttonLink?.story?.url || buttonLink?.url || buttonLink?.cached_url;

      if (name && url) {
        items.push({ name, url });
      }
    }
  }

  return items;
}

function extractDestinationsFromCarousel(
  carousel: Carousel,
  baseUrl: string,
): Array<{ name: string; url: string }> {
  const items: Array<{ name: string; url: string }> = [];

  if (!carousel.cards) return items;

  for (const card of carousel.cards) {
    if (card.component === "cardLocations") {
      const locationCard = card as CardLocations;
      const name = locationCard.title;
      const link = unwrapLink(locationCard.link);

      let url = "";
      if (link?.story?.url) {
        url = `${baseUrl}/${link.story.url}`;
      } else if (link?.cached_url) {
        url = `${baseUrl}/${link.cached_url}`;
      } else if (link?.url) {
        url = link.url;
      }

      if (name && url) {
        items.push({ name, url });
      }
    }
  }

  return items;
}

function extractOpeningHoursFromLinedListSection(
  section: LinedListSection,
): Array<{ day: string; hours: string }> {
  const hours: Array<{ day: string; hours: string }> = [];

  if (!section.items) return hours;

  for (const item of section.items) {
    if (item.component === "linedListItem") {
      const listItem = item as LinedListItem;
      const day = listItem.title;
      const hoursValue = listItem.price;

      if (day && hoursValue) {
        hours.push({ day, hours: hoursValue });
      }
    }
  }

  return hours;
}

function extractAmenitiesFromLocationCard(
  locationCard: LocationCard,
): string[] {
  const amenities: string[] = [];

  if (!locationCard.amenities) return amenities;

  for (const amenity of locationCard.amenities) {
    const amenityContent = amenity as {
      content?: { title?: string };
      name?: string;
    };
    const title = amenityContent.content?.title || amenityContent.name;
    if (title) {
      amenities.push(title);
    }
  }

  return amenities;
}

function findSectionsInContent(content: LocationPage["content"]): {
  heroWide: HeroWide | null;
  faqSection: FaqSection | null;
  faqLinedListSection: LinedListSection | null;
  parkingLinedListSection: LinedListSection | null;
  parkingCarousel: Carousel | null;
  destinationsCarousel: Carousel | null;
  locationCard: LocationCard | null;
  operatingHoursSection: LinedListSection | null;
} {
  let heroWide: HeroWide | null = null;
  let faqSection: FaqSection | null = null;
  let faqLinedListSection: LinedListSection | null = null;
  let parkingLinedListSection: LinedListSection | null = null;
  let parkingCarousel: Carousel | null = null;
  let destinationsCarousel: Carousel | null = null;
  let locationCard: LocationCard | null = null;
  let operatingHoursSection: LinedListSection | null = null;

  if (!content)
    return {
      heroWide,
      faqSection,
      faqLinedListSection,
      parkingLinedListSection,
      parkingCarousel,
      destinationsCarousel,
      locationCard,
      operatingHoursSection,
    };

  for (const section of content) {
    if (section.component === "heroWide" && !heroWide) {
      heroWide = section as HeroWide;
    }
    if (section.component === "locationCard" && !locationCard) {
      locationCard = section as LocationCard;
    }
    if (section.component === "faqSection" && !faqSection) {
      faqSection = section as FaqSection;
    }
    if (section.component === "linedListSection") {
      const linedList = section as LinedListSection;

      const sectionTitle = linedList.typography?.[0]?.title?.[0];
      const titleContent =
        (sectionTitle as Title)?.content?.toLowerCase() || "";

      if (titleContent.includes("operating hours") && !operatingHoursSection) {
        operatingHoursSection = linedList;
        continue;
      }

      const hasAccordionItems = linedList.items?.some(
        (item) => item.component === "accordion",
      );
      if (
        linedList.type === "split" &&
        hasAccordionItems &&
        !faqLinedListSection
      ) {
        faqLinedListSection = linedList;
        continue;
      }

      if (linedList.type === "default" && !parkingLinedListSection) {
        const hasDetailedItems = linedList.items?.some(
          (item) => item.component === "linedListItemDetailed",
        );
        if (hasDetailedItems) {
          parkingLinedListSection = linedList;
        }
      }
    }
    if (section.component === "carousel") {
      const carousel = section as Carousel;

      const hasParkingCards = carousel.cards?.some(
        (card) => card.component === "cardParking",
      );
      if (hasParkingCards && !parkingCarousel) {
        parkingCarousel = carousel;
        continue;
      }

      const hasLocationCards = carousel.cards?.some(
        (card) => card.component === "cardLocations",
      );
      if (hasLocationCards && !destinationsCarousel) {
        destinationsCarousel = carousel;
      }
    }
  }

  return {
    heroWide,
    faqSection,
    faqLinedListSection,
    parkingLinedListSection,
    parkingCarousel,
    destinationsCarousel,
    locationCard,
    operatingHoursSection,
  };
}

export function LocationPageSchema({
  blok,
  pathname,
}: LocationPageSchemaProps) {
  const baseUrl = process.env.NEXT_PUBLIC_DOMAIN || "";
  const pageUrl = `${baseUrl}${pathname}`;

  const locationData = parseLocationUrl(pathname);

  const {
    heroWide,
    faqSection,
    faqLinedListSection,
    parkingLinedListSection,
    parkingCarousel,
    destinationsCarousel,
    locationCard,
    operatingHoursSection,
  } = findSectionsInContent(blok.content);

  const pageName =
    locationCard?.title ||
    (heroWide && extractNameFromHeroWide(heroWide)) ||
    (blok.seoTitle && blok.seoTitle.trim()) ||
    null;

  const seoDescription =
    blok.seoDescription && blok.seoDescription.trim()
      ? blok.seoDescription
      : undefined;

  const schemas: (SchemaWebPage | SchemaFAQPage | SchemaItemList)[] = [];

  if (locationData.type === "city" && locationData.regionCode && pageName) {
    const webPageSchema = generateCityPageSchema({
      cityName: pageName,
      locationData,
      pageUrl,
      description: seoDescription,
    });
    schemas.push(webPageSchema);
  }

  if (
    locationData.type === "neighborhood" &&
    locationData.regionCode &&
    pageName
  ) {
    const cityName = locationData.citySlug
      ? slugToName(locationData.citySlug.replace(/-parking$/, ""))
      : undefined;
    const pageDescription =
      seoDescription ||
      extractDescriptionFromInfoBlock(blok.content) ||
      undefined;

    const webPageSchema = generateNeighborhoodPageSchema({
      neighborhoodName: pageName,
      cityName,
      locationData,
      pageUrl,
      description: pageDescription,
    });
    schemas.push(webPageSchema);

    if (parkingLinedListSection) {
      const parkingItems = extractParkingOptionsFromLinedListSection(
        parkingLinedListSection,
      );
      if (parkingItems.length > 0) {
        const parkingListSchema = generateParkingItemListSchema({
          items: parkingItems,
        });
        schemas.push(parkingListSchema);
      }
    }

    if (destinationsCarousel) {
      const destinationItems = extractDestinationsFromCarousel(
        destinationsCarousel,
        baseUrl,
      );
      if (destinationItems.length > 0) {
        const destinationListSchema = generateDestinationItemListSchema({
          items: destinationItems,
        });
        schemas.push(destinationListSchema);
      }
    }
  }

  if (
    locationData.type === "destination" &&
    locationData.regionCode &&
    pageName
  ) {
    const cityName = locationData.citySlug
      ? slugToName(locationData.citySlug.replace(/-parking$/, ""))
      : undefined;
    const pageDescription =
      seoDescription ||
      extractDescriptionFromInfoBlock(blok.content) ||
      undefined;

    const webPageSchema = generateDestinationPageSchema({
      poiName: pageName,
      poiType: getPOITypeFromName(pageName),
      cityName,
      locationData,
      pageUrl,
      description: pageDescription,
    });
    schemas.push(webPageSchema);

    if (parkingCarousel) {
      const parkingItems = extractParkingOptionsFromCarousel(parkingCarousel);
      if (parkingItems.length > 0) {
        const parkingListSchema = generateParkingItemListSchema({
          items: parkingItems,
        });
        schemas.push(parkingListSchema);
      }
    }

    if (destinationsCarousel) {
      const destinationItems = extractDestinationsFromCarousel(
        destinationsCarousel,
        baseUrl,
      );
      if (destinationItems.length > 0) {
        const destinationListSchema = generateDestinationItemListSchema({
          items: destinationItems,
        });
        schemas.push(destinationListSchema);
      }
    }
  }

  if (locationData.type === "parkingLot" && locationCard && pageName) {
    const description =
      seoDescription ||
      extractDescriptionFromLocationCard(locationCard) ||
      undefined;
    const address = locationCard.address
      ? parseAddress(locationCard.address)
      : undefined;
    const image = locationCard.locationImage?.[0]?.asset?.filename || undefined;
    const amenities = extractAmenitiesFromLocationCard(locationCard);
    const openingHours = operatingHoursSection
      ? extractOpeningHoursFromLinedListSection(operatingHoursSection)
      : undefined;

    const webPageSchema = generateParkingLotPageSchema({
      name: pageName,
      image,
      description,
      address: address || undefined,
      locationId: locationCard.locationId || undefined,
      amenities: amenities.length > 0 ? amenities : undefined,
      openingHours:
        openingHours && openingHours.length > 0 ? openingHours : undefined,
      pageUrl,
    });
    schemas.push(webPageSchema);
  }

  let faqItems: Array<{ question: string; answer: string }> = [];

  if (faqSection) {
    faqItems = extractFaqItemsFromFaqSection(faqSection);
  } else if (faqLinedListSection) {
    faqItems = extractFaqItemsFromLinedListSection(faqLinedListSection);
  }

  if (faqItems.length > 0) {
    const faqPageSchema = generateFAQPageSchema({ items: faqItems });
    schemas.push(faqPageSchema);
  }

  if (schemas.length === 0) {
    return null;
  }

  return (
    <>
      {schemas.map((schema, index) => (
        <JsonLdScript
          key={`schema-${index}`}
          schema={schema}
          id={`schema-${Array.isArray(schema["@type"]) ? schema["@type"][0] : schema["@type"]}-${index}`}
        />
      ))}
    </>
  );
}
